/**
 * Utility functions for parsing survey answers consistently throughout the application
 */

/**
 * Parses a checkbox survey answer that may be a JSON array or undefined/null
 * @param answer The raw survey answer string
 * @param excludeNone Whether to filter out "none" values from the result
 * @returns Array of string values, or empty array if parsing fails
 */
export const parseCheckboxSurveyAnswer = (
  answer: string | null | undefined,
  excludeNone: boolean = true,
): string[] => {
  try {
    const parsed = JSON.parse(answer || "[]");
    const result = Array.isArray(parsed) ? parsed : [];
    return excludeNone ? result.filter((id) => id !== "none") : result;
  } catch (e) {
    return [];
  }
};

/**
 * Checks if a checkbox survey answer contains only "none" or is empty
 * @param answer The raw survey answer string
 * @returns True if the answer is effectively "none" selected
 */
export const isNoneSelected = (answer: string | null | undefined): boolean => {
  const parsed = parseCheckboxSurveyAnswer(answer, false);
  return parsed.length === 0 || (parsed.length === 1 && parsed[0] === "none");
};

/**
 * Gets the first selected vendor ID from a checkbox survey answer (useful for single-selection scenarios)
 * @param answer The raw survey answer string
 * @returns The first vendor ID or null if none selected
 */
export const getFirstSelectedVendor = (answer: string | null | undefined): string | null => {
  const vendorIds = parseCheckboxSurveyAnswer(answer);
  return vendorIds.length > 0 ? vendorIds[0] : null;
};
